import { useMutation } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client.js';
import { ensureOk } from '../lib/api-error.js';
import { useAuth } from '../store/auth.js';

export type Intention = 'love' | 'talk' | 'money' | 'travel' | 'career' | 'launch' | 'luck';

export interface DayMetrics {
  energy: number; luck: number; creativity: number; smoothness: number;
  overall: number; band: 'good' | 'soso' | 'bad'; obstacles: string[];
}
export interface LuckyDay {
  date: string;
  metrics: DayMetrics;
  driver: { transiting: string; natal: string; type: string; score: number } | null;
}
export interface LuckyDashboard {
  intention: Intention; horizonDays: number; hasBirthTime: boolean;
  natal: { sun: string; moon: string; ascendant?: string };
  days: LuckyDay[];
}

export interface SavedLuckyDashboard extends LuckyDashboard {
  id: string; periodStart: string; timezone: string;
  detailTexts: Record<string, string>; paidSkuRunId: string | null;
}

export function useLuckyDashboard() {
  const jwt = useAuth(s => s.jwt);
  return useMutation({
    mutationFn: async (intention: Intention) => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await ensureOk(await apiFetch('/api/v1/lucky/results', jwt, {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ intention }),
      }));
      return await res.json() as SavedLuckyDashboard;
    },
  });
}

export interface LuckyDayDetail {
  date: string;
  dateLabel: string;
  metrics: DayMetrics;
  /** LLM "why this day is lucky" narrative; null if generation failed. */
  text: string | null;
}

/** A saved day is generated only by an explicit user action. */
export function useLuckyDayDetail() {
  const jwt = useAuth(s => s.jwt);
  return useMutation({
    mutationFn: async ({ id, date }: { id: string; date: string }) => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await ensureOk(await apiFetch(`/api/v1/lucky/results/${encodeURIComponent(id)}/detail`, jwt, {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ date }),
      }));
      return await res.json() as LuckyDayDetail;
    },
  });
}
